import { useContext, useState } from 'react';

import { Button, Dialog, Icon, Pagination, Table, Tbody, Td, Th, Thead, Tr, GlobalContext } from 'lynkco-up-core';
import { GenericFunction } from 'lynkco-up-core/dist/types/types';

import { DealershipUserV1 } from '../../services/dealerships/types';
import { deleteDealershipAdmin } from '../../services/dealerships';

type TableProps = {
  data: DealershipUserV1[];
  isLoading?: boolean;
  paginationStartIndex: number;
  paginationEndIndex: number;
  paginationQuantity: number;
  onPaginationNextChange: GenericFunction;
  onPaginationPreviousChange: GenericFunction;
  onRequestUpdate: () => Promise<DealershipUserV1[]>;
};

function DealershipAdminsTable({
  data,
  isLoading = false,
  paginationStartIndex,
  paginationEndIndex,
  paginationQuantity,
  onPaginationNextChange,
  onPaginationPreviousChange,
  onRequestUpdate,
}: TableProps): JSX.Element {
  const { alert, loading } = useContext(GlobalContext);
  const hasData = data.length > 0;
  const [user2ToDelete, setUser2ToDelete] = useState<DealershipUserV1 | null>(null);
  const [isDeleteUserDialogOpen, setIsDeleteUserDialogOpen] = useState(false);

  function handleOpenDeleteUserDialog(user: DealershipUserV1) {
    setUser2ToDelete(user);
    setIsDeleteUserDialogOpen(true);
  }

  function handleCloseDeleteUserDialog() {
    setIsDeleteUserDialogOpen(false);
    setTimeout(() => {
      setUser2ToDelete(null);
    }, 200);
  }

  async function handleDeleteUser(user: DealershipUserV1) {
    if (!user) {
      alert.show('Failed to delete user.', 'error');
      return;
    }

    try {
      loading.show();
      await deleteDealershipAdmin(user);
      alert.show(
        `User: ${user.firstName} Email: ${user.email} UserId: ${user.id} was deleted successfully.`,
        'success',
      );
    } catch (err) {
      alert.show('Failed to delete user.', 'error');
    }

    handleCloseDeleteUserDialog();
    onRequestUpdate();
    loading.hide();
  }

  return (
    <div className="z-10 bookings-table">
      <div className="overflow-x-scroll">
        <Table>
          <Thead>
            <Tr>
              <Th extraClasses="w-3/4">
                <span className="font-medium pl-6">EMAIL</span>
              </Th>
              <Th>
                <span className="font-medium">USERID</span>
              </Th>
              <Th>
                <span className="font-medium">FIRSTNAME</span>
              </Th>
              <Th>
                <span className="sr-only">DELETE USER</span>
              </Th>
            </Tr>
          </Thead>
          <Tbody>
            {data != undefined ? (
              data.map(user => (
                <Tr key={user.id}>
                  <Td>
                    <span className="font-medium pl-6 text-black">{user.email}</span>
                  </Td>
                  <Td>
                    <span className="font-medium pl-6 text-black">{user.id}</span>
                  </Td>
                  <Td>
                    <span className="font-medium pl-6 text-black">{user.firstName}</span>
                  </Td>
                  <Td cellWidth={100}>
                    <div className="flex items-center">
                      <Button onClick={() => handleOpenDeleteUserDialog(user)} variant="transparent" size="small">
                        <Icon size="small" name="person_remove" />
                      </Button>
                    </div>
                  </Td>
                </Tr>
              ))
            ) : (
              <div></div>
            )}
          </Tbody>
        </Table>
        {!isLoading && !hasData && <p className="text-center text-2xl mt-8 pl-8 mb-8 text-gray-500">No users found</p>}
        <Dialog
          icon={'warning'}
          title="Remove Dealership Administrator"
          isOpen={isDeleteUserDialogOpen}
          body={`Are you sure you want to remove ${user2ToDelete?.firstName} as Dealership Administrator?`}
          onClose={handleCloseDeleteUserDialog}
          onCancel={handleCloseDeleteUserDialog}
          onConfirm={() => handleDeleteUser(user2ToDelete as DealershipUserV1)}
          confirmText="Yes"
          cancelText="Cancel"></Dialog>
      </div>
      {!isLoading && hasData && (
        <div className="px-2.5">
          <Pagination
            itemsStartIndex={paginationStartIndex}
            itemsEndIndex={paginationEndIndex}
            itemsQuantity={paginationQuantity}
            onNextChange={onPaginationNextChange}
            onPreviousChange={onPaginationPreviousChange}
          />
        </div>
      )}
    </div>
  );
}

export default DealershipAdminsTable;
